import { Spinner } from '@chakra-ui/react';
import { forwardRef, ReactNode, useCallback, useImperativeHandle } from 'react';
import {
  MBox,
  MButton,
  MCenter,
  MFlex,
  MGrid,
  MGridItem,
  MText,
} from '../../../../../components/Monetize';
import useStepperAction from './hook/useStepperAction';

interface StepperFooterAction {
  label: string;
  loading?: boolean;
  disable?: boolean;
  testId?: string;
  onClick?: (
    event: (forcedIndex?: number) => void,
    resetStepper?: () => void,
  ) => void;
}

interface StepperComponentProps {
  content: Array<{
    label: ReactNode;
    item: JSX.Element;
    footer: {
      prev?: StepperFooterAction;
      /**
       * Still reachable through `triggerNext` when hidden, for steps whose
       * content carries its own next button
       */
      next: StepperFooterAction & { hidden?: boolean };
      skip?: StepperFooterAction;
      /** Shown after the buttons, e.g. why next is disabled */
      note?: ReactNode;
    };
  }>;
  defaultIndex?: number;
  loading?: boolean;
  onNextStep?: () => void;
  onBackStep?: () => void;
}

export interface StepperComponentRef {
  triggerNext: () => void;
  triggerPrev: () => void;
  triggerSkip: () => void;
}

export const StepperComponent = forwardRef<
  StepperComponentRef,
  StepperComponentProps
>(({ content, defaultIndex = 0, loading, onNextStep, onBackStep }, ref) => {
  const {
    currentIndex,
    canMovePrev,
    canMoveNext,
    moveNext,
    movePrev,
    moveIndex,
    resetStepper,
  } = useStepperAction({
    defaultIndex,
    totalSteps: content.length,
    onNextStep,
    onBackStep,
  });

  const handleIndexChange = useCallback(
    (type: 'next' | 'prev' | 'skip') => {
      const footer = content[currentIndex]?.footer;
      const onClick = footer?.[type]?.onClick;
      const step = type === 'prev' ? -1 : 1;

      // If custom onClick is provided, call it with moveIndex and resetStepper
      if (typeof onClick === 'function') {
        onClick(
          (forcedIndex?: number) => moveIndex(forcedIndex ?? step),
          resetStepper,
        );
        return;
      }

      // Default behavior: move to next/prev
      if (type === 'next' && canMoveNext) {
        moveNext();
      } else if (type === 'prev' && canMovePrev) {
        movePrev();
      }
    },
    [
      content,
      currentIndex,
      canMoveNext,
      canMovePrev,
      moveNext,
      movePrev,
      moveIndex,
      resetStepper,
    ],
  );

  const handleNextIndex = useCallback(
    () => handleIndexChange('next'),
    [handleIndexChange],
  );
  const handlePrevIndex = useCallback(
    () => handleIndexChange('prev'),
    [handleIndexChange],
  );
  const handleSkipIndex = useCallback(
    () => handleIndexChange('skip'),
    [handleIndexChange],
  );

  // Expose stepper controls through ref
  useImperativeHandle(
    ref,
    () => ({
      triggerNext: handleNextIndex,
      triggerPrev: handlePrevIndex,
      triggerSkip: handleSkipIndex,
    }),
    [handleNextIndex, handlePrevIndex, handleSkipIndex],
  );

  if (loading) {
    return (
      <MCenter>
        <Spinner />
      </MCenter>
    );
  }

  const { label, item, footer } = content[currentIndex];

  return (
    <MGrid
      // The first step is a single picker; later steps use the full width
      templateColumns={currentIndex === 0 ? '0fr 0.5fr 1fr' : '0fr 1fr'}
      gap={4}
    >
      <MGridItem>
        <MText fontWeight="semibold">{currentIndex + 1}. </MText>
      </MGridItem>
      <MGridItem>
        <MBox>
          <MBox mb={4} fontWeight="semibold">
            {label}
          </MBox>
          <MBox mb={4}>{item}</MBox>
          <MFlex align="center">
            {footer.prev && (
              <MButton
                w={20}
                isLoading={footer.prev.loading}
                isDisabled={!canMovePrev || footer.prev.disable}
                variant="secondary"
                mr={2}
                data-testid={footer.prev.testId}
                onClick={handlePrevIndex}
              >
                {footer.prev.label}
              </MButton>
            )}
            {!footer.next.hidden && (
              <MButton
                mr={2}
                minW={20}
                isLoading={footer.next.loading}
                isDisabled={footer.next.disable}
                data-testid={footer.next.testId}
                onClick={handleNextIndex}
              >
                {footer.next.label}
              </MButton>
            )}
            {footer.skip && (
              <MButton
                variant={'tertiary'}
                minW="2rem"
                isLoading={footer.skip.loading}
                isDisabled={footer.skip.disable}
                data-testid={footer.skip.testId}
                onClick={handleSkipIndex}
              >
                {footer.skip.label}
              </MButton>
            )}
            {footer.note}
          </MFlex>
        </MBox>
      </MGridItem>
    </MGrid>
  );
});

StepperComponent.displayName = 'StepperComponent';
